import type { IconType } from "@circle-react-shared/Icon";
import { settingsNavPathV3 } from "@circle-react/helpers/urlHelpers";
import { connectRoutes } from "@circle-react/lib/routes/connect";
import { settingsRoutes } from "@circle-react/lib/routes/settings";
import { siteRoutes } from "@circle-react/lib/routes/site";
import type { IconName } from "@circleco/compass/components/Icon";

import { t } from "@/i18n-js/instance";

// Nearly every item's `description` shares this key prefix - shorthand to
// keep each entry to one line.
const settingsDescription = (key: string) =>
  t(`circle_ai_dock.settings.landing.descriptions.${key}`);

export interface SettingsItemSpec {
  /** Fallback paths checked (in order) when `path` is missing or disabled. */
  alternatePaths?: string[];
  description?: string;
  icon?: IconName;
  labelOverride?: string;
  path: string;
  /** ISO date. Shows the "New" badge for 6 months after; safe to delete after that. */
  releasedAt?: string;
}

export interface SettingsGroupSpec {
  compassIcon: IconName;
  icon: IconType;
  id: string;
  items: SettingsItemSpec[];
  label: string;
}

/**
 * Settings-page groups ordered to match the new IA. `buildSettingsNavItems`
 * resolves each item against the shared
 * settings-nav links. This is a function so `t()` runs after locale loading.
 */
export const getSettingsGroupSpecs = (): SettingsGroupSpec[] => [
  {
    id: "subscription",
    label: t("circle_ai_dock.settings.groups.subscription"),
    icon: "20-plans",
    compassIcon: "credit-card",
    items: [
      {
        path: settingsNavPathV3.plans.general,
        icon: "checklist",
        labelOverride: t("circle_ai_dock.settings.items.plans"),
        description: settingsDescription("plans"),
      },
      {
        path: settingsNavPathV3.plans.billing,
        labelOverride: t("circle_ai_dock.settings.items.billing"),
        icon: "credit-card",
        description: settingsDescription("billing"),
      },
      {
        path: settingsNavPathV3.plans.payment_details,
        icon: "credit-card",
        description: settingsDescription("payment"),
      },
      {
        path: settingsNavPathV3.plans.invoices,
        icon: "checklist",
        description: settingsDescription("invoices"),
      },
      {
        // Upsell only — when on Plus, Customize/Lite live under Build → Branded
        // App and must not be claimed as settings-owned paths.
        path: settingsNavPathV3.plans.brandedApps,
        labelOverride: t("settings_nav_v3.plans.plus_label"),
        icon: "phone",
        description: settingsDescription("circle_plus"),
      },
    ],
  },
  {
    id: "audience",
    label: t("circle_ai_dock.settings.groups.audience"),
    icon: "20-members",
    compassIcon: "group",
    items: [
      {
        path: settingsNavPathV3.members.onboarding,
        icon: "steps",
        description: settingsDescription("onboarding"),
      },
      {
        path: settingsNavPathV3.members.gamification,
        icon: "trophy",
        description: settingsDescription("gamification"),
      },
      {
        path: settingsNavPathV3.members.bulk_logs,
        icon: "notes",
        description: settingsDescription("bulk_member_actions"),
      },
      {
        path: settingsNavPathV3.members.activity_logs,
        icon: "chart",
        description: settingsDescription("activity_logs"),
      },
      {
        path: connectRoutes.communitySettings(),
        icon: "people-sparkle",
        labelOverride: t("circle_ai_dock.settings.items.connect_settings"),
        description: settingsDescription("connect"),
      },
    ],
  },
  {
    id: "community",
    label: t("circle_ai_dock.settings.groups.community"),
    icon: "20-nav-settings",
    compassIcon: "settings-gear",
    items: [
      {
        path: settingsRoutes.theme(),
        icon: "color-swatch",
        labelOverride: t("customize_theme.title"),
        description: settingsDescription("theme"),
      },
      {
        path: settingsNavPathV3.content.moderation,
        icon: "shield-check",
        description: settingsDescription("moderation"),
      },
      {
        path: settingsNavPathV3.content.posts_bulk_actions,
        icon: "notes",
        description: settingsDescription("bulk_logs"),
      },
      {
        path: settingsRoutes.weeklyDigest(),
        icon: "email",
        description: settingsDescription("weekly_digest"),
      },
      {
        path: settingsRoutes.mobileApp(),
        icon: "phone",
        description: settingsDescription("mobile_app"),
      },
      {
        path: settingsRoutes.embed(),
        icon: "code-brackets",
        description: settingsDescription("embed"),
      },
      {
        path: settingsRoutes.singleSignOn(),
        icon: "lock",
        description: settingsDescription("sso"),
      },
      {
        path: settingsRoutes.integrations(),
        icon: "settings-gear",
        description: settingsDescription("integrations"),
      },
    ],
  },
  {
    id: "payments",
    label: t("circle_ai_dock.settings.groups.payments"),
    icon: "20-payments",
    compassIcon: "dollar",
    items: [
      {
        path: settingsNavPathV3.paywalls.taxes,
        icon: "receipt-tax",
        description: settingsDescription("taxes"),
      },
      {
        path: settingsNavPathV3.paywalls.bulk_logs,
        icon: "arrow-box-up",
        labelOverride: t("circle_ai_dock.build.payment_logs"),
        description: settingsDescription("export_logs"),
      },
      {
        path: settingsNavPathV3.paywalls.payment_settings,
        icon: "payment",
        labelOverride: t("circle_ai_dock.build.payment_settings"),
        description: settingsDescription("payment_settings"),
      },
      {
        path: settingsRoutes.conversionTracking(),
        icon: "chart",
        labelOverride: t("circle_ai_dock.settings.items.conversion_tracking"),
        description: settingsDescription("conversion_tracking"),
        releasedAt: "2026-10-19",
      },
      {
        path: settingsNavPathV3.affiliates.settings,
        icon: "affiliates",
        labelOverride: t("circle_ai_dock.settings.items.affiliate_settings"),
        description: settingsDescription("affiliate_settings"),
      },
    ],
  },
  {
    id: "site",
    label: t("website"),
    icon: "20-site",
    compassIcon: "earth",
    items: [
      {
        path: settingsRoutes.general(),
        icon: "settings-gear",
        description: settingsDescription("general"),
      },
      {
        path: settingsRoutes.customDomain(),
        icon: "earth",
        description: settingsDescription("custom_domain"),
      },
      {
        path: siteRoutes.seo(),
        icon: "page-search",
        description: settingsDescription("seo"),
      },
      {
        path: siteRoutes.redirects(),
        icon: "wrap-right",
        description: settingsDescription("redirects"),
      },
      {
        path: siteRoutes.index(),
        icon: "page",
        description: settingsDescription("defaults"),
      },
      {
        path: settingsRoutes.legal(),
        icon: "file",
        description: settingsDescription("legal"),
      },
    ],
  },
  {
    id: "marketing",
    label: t("circle_ai_dock.settings.groups.marketing"),
    icon: "20-send",
    compassIcon: "paper-plane",
    items: [
      {
        path: settingsNavPathV3.email.settings,
        icon: "email",
        labelOverride: t("circle_ai_dock.settings.items.email_settings"),
        description: settingsDescription("email_settings"),
      },
    ],
  },
  {
    id: "developers",
    label: t("circle_ai_dock.settings.groups.developers"),
    icon: "20-code-tag",
    compassIcon: "code-syntax",
    items: [
      {
        path: settingsNavPathV3.developers.overview,
        icon: "code-brackets",
        description: settingsDescription("developers_overview"),
      },
      {
        path: settingsNavPathV3.developers.api,
        icon: "vibe-coder",
        description: settingsDescription("tokens"),
      },
    ],
  },
];
